import React, { useState, useEffect, useMemo } from 'react';
import {
  Search,
  Users,
  User,
  DollarSign,
  TrendingUp,
  Eye,
  AlertCircle,
  Loader
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from "../../services/apiClient.ts";

const Home = () => {
  const { t } = useTranslation();
  const [groupSearch, setGroupSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  
  // State for API data
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const TENANT_ID = 'default-tenant-1';

  // Fetch groups data
  const fetchGroups = async () => {
    try {
      const response = await api.get('/v1/groups', {
        params: { per_page: 50 },
        headers: { 'X-Tenant': TENANT_ID }
      });
      return response.data.data || response.data;
    } catch (err) {
      console.error('Error fetching groups:', err);
      throw err;
    }
  };

  // Fetch travelers data
  const fetchTravelers = async () => {
    try {
      const response = await api.get('/v1/travelers', {
        params: { per_page: 50 },
        headers: { 'X-Tenant': TENANT_ID }
      });
      return response.data.data || response.data;
    } catch (err) {
      console.error('Error fetching travelers:', err);
      throw err;
    }
  };

  // Fetch group details
  const fetchGroupDetails = async () => {
    try {
      const response = await api.get('/v1/group-details', {
        params: { per_page: 100 },
        headers: { 'X-Tenant': TENANT_ID }
      });
      return response.data.data || response.data;
    } catch (err) {
      console.error('Error fetching group details:', err);
      throw err;
    }
  };

  // Load all data on component mount
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);

      try {
        const [groupsData, travelersData, groupDetailsData] = await Promise.all([
          fetchGroups(),
          fetchTravelers(),
          fetchGroupDetails()
        ]);

        // Process groups data
        const processedGroups = groupsData.map(group => {
          // Count members in this group
          const groupMembers = groupDetailsData.filter(
            detail => detail.gr_id === group.id
          );
          
          const memberCount = groupMembers.length;
          
          // Calculate total amount for this group
          const totalAmount = groupMembers.reduce(
            (sum, detail) => sum + (parseFloat(detail.total_price) || 0),
            0
          );

          // Determine status based on gr_in and gr_out
          let status = 'active';
          if (parseFloat(group.gr_in || 0) === 0 && parseFloat(group.gr_out || 0) === 0) {
            status = 'pending';
          } else if (parseFloat(group.gr_in || 0) >= totalAmount) {
            status = 'completed';
          }

          return {
            id: group.id,
            groupName: group.gr_notes || `${t('home.group')} ${group.id}`,
            date: group.created_at ? new Date(group.created_at).toLocaleDateString('ar-IQ') : '-',
            memberCount,
            totalAmount,
            status,
            gr_in: parseFloat(group.gr_in || 0),
            gr_out: parseFloat(group.gr_out || 0),
            notes: group.gr_notes
          };
        });

        setGroups(processedGroups);

        // Process travelers data
        const processedTravelers = travelersData.map(traveler => {
          // Find their group details
          const travelerGroups = groupDetailsData.filter(
            detail => detail.tr_id === traveler.id
          );

          // Calculate total balance (total_price - paid)
          const totalPrice = travelerGroups.reduce(
            (sum, detail) => sum + (parseFloat(detail.total_price) || 0),
            0
          );

          const totalPaid = travelerGroups.reduce(
            (sum, detail) => sum + (parseFloat(detail.cash) || 0),
            0
          );

          const totalBalance = totalPrice - totalPaid;

          // Determine if active today
          const lastActivity = traveler.updated_at || traveler.created_at;
          const isActiveToday = lastActivity && 
            new Date(lastActivity).toDateString() === new Date().toDateString();

          return {
            id: traveler.id,
            userName: traveler.name,
            status: isActiveToday ? 'active' : 'inactive',
            totalBalance,
            lastActivity: lastActivity ? new Date(lastActivity).toISOString() : new Date().toISOString(),
            phone1: traveler.phone1,
            passport: traveler.passport
          };
        });

        setUsers(processedTravelers);

      } catch (err) {
        setError(err.response?.data?.message || err.message || 'حدث خطأ في تحميل البيانات');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [t]);

  // Filter groups based on search
  const filteredGroups = useMemo(() => {
    return groups.filter(group =>
      group.groupName.toLowerCase().includes(groupSearch.toLowerCase()) ||
      group.date.includes(groupSearch) ||
      (group.status && group.status.toLowerCase().includes(groupSearch.toLowerCase()))
    );
  }, [groupSearch, groups]);

  // Filter active users based on search
  const filteredUsers = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return users.filter(user => {
      const isActiveToday = (user.lastActivity && user.lastActivity.startsWith(today)) || 
                           user.status === 'active';
      const matchesSearch = (user.userName && user.userName.toLowerCase().includes(userSearch.toLowerCase())) ||
                          (user.status && user.status.toLowerCase().includes(userSearch.toLowerCase()));
      return isActiveToday && matchesSearch;
    });
  }, [userSearch, users]);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalGroups = groups.length;
    const activeGroups = groups.filter(g => g.status === 'active').length;
    const totalUsers = users.length;
    const activeUsers = users.filter(u => u.status === 'active').length;
    const totalRevenue = groups.reduce((sum, group) => sum + group.totalAmount, 0);
    
    return {
      totalGroups,
      activeGroups,
      totalUsers,
      activeUsers,
      totalRevenue
    };
  }, [groups, users]);

  const getStatusColor = (status) => {
    const colorMap = {
      'active': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      'inactive': 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
      'pending': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      'completed': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      'cancelled': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
      'default': 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
    };
    return colorMap[status] || colorMap.default;
  };

  const StatCard = ({ title, value, icon, bgColor = 'bg-blue-50 dark:bg-blue-900/20', iconColor = 'text-blue-600 dark:text-blue-400' }) => (
    <div className={`${bgColor} rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700 dark:bg-gray-800 min-h-[110px]`}>
      <div className="flex items-center gap-4 rtl:flex-row-reverse">
        <div className="flex-1 min-w-0 rtl:text-right">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-1 leading-snug break-words">
            {title}
          </p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white whitespace-nowrap">
            {typeof value === 'number' && title.includes(t('home.totalRevenue')) 
              ? `$${value.toLocaleString()}` 
              : value}
          </p>
        </div>
        <div className={`${iconColor} p-3 rounded-lg bg-white dark:bg-gray-700 shadow-sm flex-shrink-0 flex items-center justify-center`}>
          {icon}
        </div>
      </div>
    </div>
  );

  // Loading state
  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader className="animate-spin h-12 w-12 text-blue-600 dark:text-blue-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-300">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 flex items-center gap-3">
          <AlertCircle className="text-red-600 dark:text-red-400" size={24} />
          <div>
            <h3 className="text-red-800 dark:text-red-300 font-semibold mb-1">
              خطأ في تحميل البيانات
            </h3>
            <p className="text-red-600 dark:text-red-400">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              إعادة المحاولة
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 rtl:text-right">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">
        {t('home.dashboardOverview')}
      </h1>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-6 mb-8">
        <StatCard
          title={t('home.totalGroups')}
          value={stats.totalGroups}
          icon={<Users size={24} />}
          bgColor="bg-blue-50 dark:bg-blue-900/20"
          iconColor="text-blue-600 dark:text-blue-400"
        />
        <StatCard
          title={t('home.activeGroups')}
          value={stats.activeGroups}
          icon={<TrendingUp size={24} />}
          bgColor="bg-green-50 dark:bg-green-900/20"
          iconColor="text-green-600 dark:text-green-400"
        />
        <StatCard
          title={t('home.totalUsers')}
          value={stats.totalUsers}
          icon={<User size={24} />}
          bgColor="bg-cyan-50 dark:bg-cyan-900/20"
          iconColor="text-cyan-600 dark:text-cyan-400"
        />
        <StatCard
          title={t('home.activeUsers')}
          value={stats.activeUsers}
          icon={<User size={24} />}
          bgColor="bg-green-50 dark:bg-green-900/20"
          iconColor="text-green-600 dark:text-green-400"
        />
        <StatCard
          title={t('home.totalRevenue')}
          value={stats.totalRevenue}
          icon={<DollarSign size={24} />}
          bgColor="bg-yellow-50 dark:bg-yellow-900/20"
          iconColor="text-yellow-600 dark:text-yellow-400"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Monthly Groups Table */}
        <div className="lg:col-span-7">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4 rtl:flex-row-reverse">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {t('home.monthlyGroups')}
              </h2>
              <div className="relative">
                <Search className="absolute left-3 rtl:left-auto rtl:right-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={20} />
                <input
                  type="text"
                  placeholder={t('home.searchGroups')}
                  value={groupSearch}
                  onChange={(e) => setGroupSearch(e.target.value)}
                  className="pl-10 rtl:pl-4 rtl:pr-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                />
              </div>
            </div>
            
            <div className="app-table-wrap">
              <table className="app-table w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left rtl:text-right py-3 px-4 font-semibold text-gray-900 dark:text-white">{t('home.groupName')}</th>
                    <th className="text-left rtl:text-right py-3 px-4 font-semibold text-gray-900 dark:text-white">{t('home.date')}</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-900 dark:text-white">{t('home.members')}</th>
                    <th className="text-right rtl:text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">{t('home.amount')}</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-900 dark:text-white">{t('home.status')}</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-900 dark:text-white">{t('home.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredGroups.map((group) => (
                    <tr key={group.id} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                      <td className="py-3 px-4">
                        <span className="font-medium text-gray-900 dark:text-white">
                          {group.groupName}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-300">{group.date}</td>
                      <td className="py-3 px-4 text-center">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                          {group.memberCount}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right rtl:text-left font-medium text-gray-900 dark:text-white">
                        ${group.totalAmount.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(group.status)}`}>
                          {t(`home.status_${group.status}`) || group.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <button className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors" title={t('home.viewDetails')}>
                          <Eye size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {filteredGroups.length === 0 && (
              <div className="text-center py-8">
                <p className="text-gray-500 dark:text-gray-400">
                  {t('home.noGroupsFound')}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Active Users Today Table */}
        <div className="lg:col-span-5">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4 rtl:flex-row-reverse">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {t('home.usersActiveToday')}
              </h2>
              <div className="relative">
                <Search className="absolute left-3 rtl:left-auto rtl:right-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={20} />
                <input
                  type="text"
                  placeholder={t('home.searchUsers')}
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="pl-10 rtl:pl-4 rtl:pr-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-52 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                />
              </div>
            </div>
            
            <div className="app-table-wrap">
              <table className="app-table w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left rtl:text-right py-3 px-4 font-semibold text-gray-900 dark:text-white">{t('home.userName')}</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-900 dark:text-white">{t('home.status')}</th>
                    <th className="text-right rtl:text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">{t('home.balance')}</th>
                    <th className="text-left rtl:text-right py-3 px-4 font-semibold text-gray-900 dark:text-white">{t('home.lastActivity')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                      <td className="py-3 px-4">
                        <span className="font-medium text-gray-900 dark:text-white">
                          {user.userName}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(user.status)}`}>
                          {t(`home.status_${user.status}`) || user.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right rtl:text-left">
                        <span className={`font-medium ${
                          user.totalBalance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                        }`}>
                          ${Math.abs(user.totalBalance).toLocaleString()}
                          {user.totalBalance < 0 ? '-' : ''}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-300">
                        {user.lastActivity.slice(11, 16)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {filteredUsers.length === 0 && (
              <div className="text-center py-8">
                <p className="text-gray-500 dark:text-gray-400">
                  {t('home.noActiveUsersFound')}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;


// import React, { useState, useMemo } from 'react';
// import {
//   Search,
//   Users,
//   User,
//   DollarSign,
//   TrendingUp,
//   Eye
// } from 'lucide-react';
// import { useTranslation } from 'react-i18next';
// import { mockGroups, mockUsers, groupStatuses, userStatuses } from '../../data/mockData';

// const Home = () => {
//   const { t } = useTranslation();
//   const [groupSearch, setGroupSearch] = useState('');
//   const [userSearch, setUserSearch] = useState('');

//   // Filter groups based on search
//   const filteredGroups = useMemo(() => {
//     return mockGroups.filter(group =>
//       group.groupName.toLowerCase().includes(groupSearch.toLowerCase()) ||
//       group.date.includes(groupSearch) ||
//       group.status.toLowerCase().includes(groupSearch.toLowerCase())
//     );
//   }, [groupSearch]);

//   // Filter active users based on search
//   const filteredUsers = useMemo(() => {
//     const today = new Date().toISOString().slice(0, 10);
//     return mockUsers
//       .filter(user => {
//         const isActiveToday = (user.lastActivity && user.lastActivity.startsWith(today)) || user.status === 'active';
//         const matchesSearch = (user.userName && user.userName.toLowerCase().includes(userSearch.toLowerCase())) ||
//                             (user.status && user.status.toLowerCase().includes(userSearch.toLowerCase()));
//         return isActiveToday && matchesSearch;
//       });
//   }, [userSearch]);

//   // Calculate statistics
//   const stats = useMemo(() => {
//     const totalGroups = mockGroups.length;
//     const activeGroups = mockGroups.filter(g => g.status === 'active').length;
//     const totalUsers = mockUsers.length;
//     const activeUsers = mockUsers.filter(u => u.status === 'active').length;
//     const totalRevenue = mockGroups.reduce((sum, group) => sum + group.totalAmount, 0);
    
//     return {
//       totalGroups,
//       activeGroups,
//       totalUsers,
//       activeUsers,
//       totalRevenue
//     };
//   }, []);

//   const getStatusColor = (status, type) => {
//     const statusConfig = type === 'group' ? groupStatuses : userStatuses;
//     const config = statusConfig.find(s => s.value === status);
//     const colorMap = {
//       'primary': 'bg-blue-100 text-blue-800',
//       'success': 'bg-green-100 text-green-800',
//       'warning': 'bg-yellow-100 text-yellow-800',
//       'error': 'bg-red-100 text-red-800',
//       'info': 'bg-cyan-100 text-cyan-800',
//       'default': 'bg-gray-100 text-gray-800'
//     };
//     return colorMap[config?.color] || colorMap.default;
//   };

//   const StatCard = ({ title, value, icon, bgColor = 'bg-blue-50 dark:bg-blue-900/20', iconColor = 'text-blue-600 dark:text-blue-400' }) => (
//     <div className={`${bgColor} rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700 dark:bg-gray-800`}>
//       <div className="flex items-center justify-between rtl:flex-row-reverse">
//         <div>
//           <p className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
//             {title}
//           </p>
//           <p className="text-2xl font-bold text-gray-900 dark:text-white">
//             {typeof value === 'number' && title.includes(t('home.totalRevenue')) 
//               ? `$${value.toLocaleString()}` 
//               : value}
//           </p>
//         </div>
//         <div className={`${iconColor} p-3 rounded-lg bg-white dark:bg-gray-700 shadow-sm`}>
//           {icon}
//         </div>
//       </div>
//     </div>
//   );

//   return (
//     <div className="max-w-7xl mx-auto p-6 rtl:text-right">
//       <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">
//         {t('home.dashboardOverview')}
//       </h1>

//       {/* Statistics Cards */}
//       <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
//         <StatCard
//           title={t('home.totalGroups')}
//           value={stats.totalGroups}
//           icon={<Users size={24} />}
//           bgColor="bg-blue-50 dark:bg-blue-900/20"
//           iconColor="text-blue-600 dark:text-blue-400"
//         />
//         <StatCard
//           title={t('home.activeGroups')}
//           value={stats.activeGroups}
//           icon={<TrendingUp size={24} />}
//           bgColor="bg-green-50 dark:bg-green-900/20"
//           iconColor="text-green-600 dark:text-green-400"
//         />
//         <StatCard
//           title={t('home.totalUsers')}
//           value={stats.totalUsers}
//           icon={<User size={24} />}
//           bgColor="bg-cyan-50 dark:bg-cyan-900/20"
//           iconColor="text-cyan-600 dark:text-cyan-400"
//         />
//         <StatCard
//           title={t('home.activeUsers')}
//           value={stats.activeUsers}
//           icon={<User size={24} />}
//           bgColor="bg-green-50 dark:bg-green-900/20"
//           iconColor="text-green-600 dark:text-green-400"
//         />
//         <StatCard
//           title={t('home.totalRevenue')}
//           value={stats.totalRevenue}
//           icon={<DollarSign size={24} />}
//           bgColor="bg-yellow-50 dark:bg-yellow-900/20"
//           iconColor="text-yellow-600 dark:text-yellow-400"
//         />
//       </div>

//       <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
//         {/* Monthly Groups Table */}
//         <div className="lg:col-span-7">
//           <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
//             <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4 rtl:flex-row-reverse">
//               <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
//                 {t('home.monthlyGroups')}
//               </h2>
//               <div className="relative">
//                 <Search className="absolute left-3 rtl:left-auto rtl:right-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={20} />
//                 <input
//                   type="text"
//                   placeholder={t('home.searchGroups')}
//                   value={groupSearch}
//                   onChange={(e) => setGroupSearch(e.target.value)}
//                   className="pl-10 rtl:pl-4 rtl:pr-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
//                 />
//               </div>
//             </div>
            
//             <div className="overflow-x-auto">
//               <table className="w-full">
//                 <thead>
//                   <tr className="border-b border-gray-200 dark:border-gray-700">
//                     <th className="text-left rtl:text-right py-3 px-4 font-semibold text-gray-900 dark:text-white">{t('home.groupName')}</th>
//                     <th className="text-left rtl:text-right py-3 px-4 font-semibold text-gray-900 dark:text-white">{t('home.date')}</th>
//                     <th className="text-center py-3 px-4 font-semibold text-gray-900 dark:text-white">{t('home.members')}</th>
//                     <th className="text-right rtl:text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">{t('home.amount')}</th>
//                     <th className="text-center py-3 px-4 font-semibold text-gray-900 dark:text-white">{t('home.status')}</th>
//                     <th className="text-center py-3 px-4 font-semibold text-gray-900 dark:text-white">{t('home.actions')}</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {filteredGroups.map((group) => (
//                     <tr key={group.id} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
//                       <td className="py-3 px-4">
//                         <span className="font-medium text-gray-900 dark:text-white">
//                           {group.groupName}
//                         </span>
//                       </td>
//                       <td className="py-3 px-4 text-gray-600 dark:text-gray-300">{group.date}</td>
//                       <td className="py-3 px-4 text-center">
//                         <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
//                           {group.memberCount}
//                         </span>
//                       </td>
//                       <td className="py-3 px-4 text-right rtl:text-left font-medium text-gray-900 dark:text-white">
//                         ${group.totalAmount.toLocaleString()}
//                       </td>
//                       <td className="py-3 px-4 text-center">
//                         <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(group.status, 'group')}`}>
//                           {t(`home.status_${group.status}`)}
//                         </span>
//                       </td>
//                       <td className="py-3 px-4 text-center">
//                         <button className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors" title={t('home.viewDetails')}>
//                           <Eye size={16} />
//                         </button>
//                       </td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             </div>
            
//             {filteredGroups.length === 0 && (
//               <div className="text-center py-8">
//                 <p className="text-gray-500 dark:text-gray-400">
//                   {t('home.noGroupsFound')}
//                 </p>
//               </div>
//             )}
//           </div>
//         </div>

//         {/* Active Users Today Table */}
//         <div className="lg:col-span-5">
//           <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
//             <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4 rtl:flex-row-reverse">
//               <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
//                 {t('home.usersActiveToday')}
//               </h2>
//               <div className="relative">
//                 <Search className="absolute left-3 rtl:left-auto rtl:right-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={20} />
//                 <input
//                   type="text"
//                   placeholder={t('home.searchUsers')}
//                   value={userSearch}
//                   onChange={(e) => setUserSearch(e.target.value)}
//                   className="pl-10 rtl:pl-4 rtl:pr-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-52 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
//                 />
//               </div>
//             </div>
            
//             <div className="overflow-x-auto">
//               <table className="w-full">
//                 <thead>
//                   <tr className="border-b border-gray-200 dark:border-gray-700">
//                     <th className="text-left rtl:text-right py-3 px-4 font-semibold text-gray-900 dark:text-white">{t('home.userName')}</th>
//                     <th className="text-center py-3 px-4 font-semibold text-gray-900 dark:text-white">{t('home.status')}</th>
//                     <th className="text-right rtl:text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">{t('home.balance')}</th>
//                     <th className="text-left rtl:text-right py-3 px-4 font-semibold text-gray-900 dark:text-white">{t('home.lastActivity')}</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {filteredUsers.map((user) => (
//                     <tr key={user.id} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
//                       <td className="py-3 px-4">
//                         <span className="font-medium text-gray-900 dark:text-white">
//                           {user.userName}
//                         </span>
//                       </td>
//                       <td className="py-3 px-4 text-center">
//                         <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(user.status, 'user')}`}>
//                           {t(`home.status_${user.status}`)}
//                         </span>
//                       </td>
//                       <td className="py-3 px-4 text-right rtl:text-left">
//                         <span className={`font-medium ${
//                           user.totalBalance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
//                         }`}>
//                           ${user.totalBalance}
//                         </span>
//                       </td>
//                       <td className="py-3 px-4 text-gray-600 dark:text-gray-300">
//                         {user.lastActivity.slice(11, 16)}
//                       </td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             </div>
            
//             {filteredUsers.length === 0 && (
//               <div className="text-center py-8">
//                 <p className="text-gray-500 dark:text-gray-400">
//                   {t('home.noActiveUsersFound')}
//                 </p>
//               </div>
//             )}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default Home;